'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { type PaginationState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { type ProductItem } from '@/lib/api/products';
import { useStore } from '@/lib/api/stores';
import { useStoreBaseCurrencyRate } from '@/lib/api/currency-rates';
import { trpc } from '@/lib/api/trpc';
import { useToast } from '@/components/ui/use-toast';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';

import type { BulkEditColumnId, BulkEditRow, BulkEditRowEdits } from '../bulk-edit.types';
import { buildBulkEditStoreCurrencies, normalizeVisibleColumns } from '../bulk-edit-currencies';
import {
  applyPricePercentToRows,
  applyStatusToRows,
  buildBulkEditRows,
  buildBulkEditRowsForSave,
  collectSavePayloads,
  getBulkEditProductsForSave,
  mergeBulkEditProductCache,
  mergeRowEdit,
} from '../bulk-edit.utils';
import { DEFAULT_VISIBLE_COLUMNS } from '../bulk-edit.types';

const BULK_EDIT_SEARCH_DEBOUNCE_MS = 500;

export function useBulkEditState(storeId: string, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const { t } = useTranslation(['products', 'common']);
  const { toast } = useToast();
  const utils = trpc.useUtils();

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedProductIds, setExpandedProductIds] = useState<Set<string>>(new Set());
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Map<string, BulkEditRowEdits>>(new Map());
  const [visibleColumns, setVisibleColumns] = useState<Set<BulkEditColumnId>>(() =>
    normalizeVisibleColumns(new Set(DEFAULT_VISIBLE_COLUMNS)),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [productCache, setProductCache] = useState<Map<string, ProductItem>>(() => new Map());

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, BULK_EDIT_SEARCH_DEBOUNCE_MS);

  const trimmedSearch = debouncedSearch.trim();

  // Reset local editor state when the modal closes (component stays mounted).
  useEffect(() => {
    if (enabled) return;
    setSearchInput('');
    setDebouncedSearch('');
    setPagination({ pageIndex: 0, pageSize: 50 });
    setExpandedProductIds(new Set());
    setSelectedKeys(new Set());
    setEdits(new Map());
    setProductCache(new Map());
  }, [enabled]);

  // New search scope — drop cached pages from prior query.
  useEffect(() => {
    if (!enabled) return;
    setProductCache(new Map());
  }, [enabled, trimmedSearch]);

  const { data: store } = useStore(storeId, enabled);
  const isCreditEnabled = store?.creditSettings?.enabled ?? false;
  const { data: currencyRateData } = useStoreBaseCurrencyRate(storeId, enabled && !!storeId);

  const storeCurrencies = useMemo(
    () =>
      buildBulkEditStoreCurrencies(
        store?.presentmentCurrencies,
        store?.chargeCurrencies,
        currencyRateData?.name ?? store?.baseCurrency,
      ),
    [store?.presentmentCurrencies, store?.chargeCurrencies, store?.baseCurrency, currencyRateData?.name],
  );

  const exchangeRates = currencyRateData?.data ?? {};

  useEffect(() => {
    setVisibleColumns((prev) => normalizeVisibleColumns(prev));
  }, [storeCurrencies.allPriceCurrencies.join(',')]);

  const listQueryParams = useMemo(
    () => ({
      storeId,
      page: pagination.pageIndex,
      per_page: pagination.pageSize,
      sort: 'name' as const,
      sort_order: 'asc' as const,
      includeVariants: true,
      filters: trimmedSearch ? { name: trimmedSearch } : {},
    }),
    [storeId, pagination.pageIndex, pagination.pageSize, trimmedSearch],
  );

  const { data, isLoading, isFetching } = trpc.products.list.useQuery(listQueryParams, {
    enabled: enabled && !!storeId,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  const products = useMemo(() => data?.items ?? [], [data?.items]);

  useEffect(() => {
    if (!enabled || products.length === 0) return;
    setProductCache((prev) => mergeBulkEditProductCache(prev, products as ProductItem[]));
  }, [enabled, products]);

  const total = data?.pagination?.totalItems ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));
  const isSearchPending = searchInput.trim() !== trimmedSearch;
  const isSearchFetching = isFetching && !isLoading && trimmedSearch.length > 0;

  const productIdsKey = useMemo(
    () => products.map((p) => p.id).join(','),
    [products],
  );

  useEffect(() => {
    if (!enabled) return;
    setExpandedProductIds(new Set(productIdsKey ? productIdsKey.split(',') : []));
    setSelectedKeys(new Set());
  }, [enabled, pagination.pageIndex, trimmedSearch, productIdsKey]);

  const rows = useMemo(
    () => buildBulkEditRows(products, expandedProductIds, edits, storeCurrencies),
    [products, expandedProductIds, edits, storeCurrencies],
  );

  const resolvedVisibleColumns = visibleColumns;

  const selectableRows = useMemo(() => rows, [rows]);
  const dirtyCount = edits.size;
  const isDirty = dirtyCount > 0;

  const toggleExpanded = useCallback((productId: string) => {
    setExpandedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }, []);

  const toggleRowSelected = useCallback((key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const toggleAllSelected = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedKeys(new Set());
        return;
      }
      setSelectedKeys(new Set(selectableRows.map((r) => r.key)));
    },
    [selectableRows],
  );

  const updateCell = useCallback((key: string, patch: BulkEditRowEdits) => {
    setEdits((prev) => mergeRowEdit(prev, key, patch));
  }, []);

  const bulkAdjustPrice = useCallback(
    (percent: number) => {
      setEdits((prev) =>
        applyPricePercentToRows(rows, selectedKeys, percent, prev, storeCurrencies),
      );
    },
    [rows, selectedKeys, storeCurrencies],
  );

  const bulkSetStatus = useCallback(
    (active: boolean) => {
      setEdits((prev) => applyStatusToRows(rows, selectedKeys, active, prev));
    },
    [rows, selectedKeys],
  );

  const discardChanges = useCallback(() => {
    setEdits(new Map());
  }, []);

  const listQueryInput = listQueryParams;

  const saveChanges = useCallback(async () => {
    if (!isDirty) return;

    const productsForSave = getBulkEditProductsForSave(edits, productCache);
    const saveRows = buildBulkEditRowsForSave(edits, productCache, storeCurrencies);

    const { productUpdates, variantUpdates, variantScalarPatches } = collectSavePayloads(
      productsForSave,
      saveRows,
      edits,
      storeCurrencies,
      exchangeRates,
    );

    const totalMutations =
      productUpdates.length + variantUpdates.length + variantScalarPatches.length;

    if (totalMutations === 0) {
      setEdits(new Map());
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all([
        ...productUpdates.map((input) => utils.client.products.update.mutate(input)),
        ...variantUpdates.map((input) => utils.client.variants.update.mutate(input)),
        ...variantScalarPatches.map((input) =>
          utils.client.variants.patchScalars.mutate(input),
        ),
      ]);

      setEdits(new Map());

      await utils.products.list.invalidate(listQueryInput);
      await utils.products.list.refetch(listQueryInput);

      await Promise.all([
        utils.variants.list.invalidate(),
        utils.variants.getById.invalidate(),
      ]);

      toast({
        title: t('products:bulkEditor.saveSuccessTitle'),
        description: t('products:bulkEditor.saveSuccessDescription', {
          count: totalMutations,
        }),
      });
    } catch (error) {
      toast({
        title: t('common:error'),
        description: error instanceof Error ? error.message : t('products:bulkEditor.saveError'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    isDirty,
    edits,
    productCache,
    storeCurrencies,
    exchangeRates,
    utils,
    listQueryInput,
    toast,
    t,
  ]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      const next = value.trim();
      if (next === '') {
        setDebouncedSearch('');
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        return;
      }
      debouncedSetSearch(next);
    },
    [debouncedSetSearch],
  );

  const toggleColumn = useCallback((column: BulkEditColumnId) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });
  }, []);

  return {
    products: products as ProductItem[],
    rows,
    total,
    pageCount,
    pagination,
    setPagination,
    searchInput,
    handleSearchChange,
    isLoading,
    isFetching,
    isSearchPending,
    isSearchFetching,
    isSaving,
    isDirty,
    dirtyCount,
    selectedKeys,
    edits,
    visibleColumns: resolvedVisibleColumns,
    toggleColumn,
    toggleExpanded,
    toggleRowSelected,
    toggleAllSelected,
    updateCell,
    bulkAdjustPrice,
    bulkSetStatus,
    discardChanges,
    saveChanges,
    storeCurrencies,
    /** @deprecated use storeCurrencies.baseCurrency — kept for HMR/back-compat */
    baseCurrency: storeCurrencies.baseCurrency,
    isCreditEnabled,
  };
}

export type BulkEditState = ReturnType<typeof useBulkEditState>;
